import { useEffect, useRef, useState } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { helper } from '@myrealtrip/frontend-weblog';
import { media } from '@myrealtrip/modash';
import { Text } from '@myrealtrip/web-ui';

import useEmblaCarousel from 'embla-carousel-react';
import { useScroll } from 'motion/react';

import {
  CATEGORY_TAB_HEIGHT,
  HEADER_Z_INDEX,
  PC_SEARCH_HOME_SCROLL_PADDING_TOP,
  SEARCH_HOME_TITLE_HEIGHT,
} from '~constants/common';
import { PASS_SECTION_KEY, PASS_SECTION_TITLE } from '~constants/pass';
import { WeblogEventName, WeblogPageCategory, WeblogScreenName } from '~constants/weblog';
import useDetectSticky from '~hooks/useDetectSticky';
import { useDetermineMobile } from '@myrealtrip/device-detect';
import useTransportProducts from '~queries/transportation/pointToPointTicket/useTransportProducts';
import { classifyProductsByCountry } from '~utils/pass/classifiedPassProducts';

import type { PassSectionKey } from '~types/pass';

const { sendClickLog } = helper;

const MOBILE_HEADER_START_POINT = SEARCH_HOME_TITLE_HEIGHT + CATEGORY_TAB_HEIGHT;
const PC_HEADER_START_POINT =
  PC_SEARCH_HOME_SCROLL_PADDING_TOP + SEARCH_HOME_TITLE_HEIGHT + CATEGORY_TAB_HEIGHT;

interface Props {
  scrollTo: (sectionKey: PassSectionKey) => void;
  inViewSectionKey: PassSectionKey;
}

const PassHeader = ({ scrollTo, inViewSectionKey }: Props) => {
  const { data: transportProducts, isSuccess } = useTransportProducts();

  const [possibleCountries, setPossibleCountries] = useState<PassSectionKey[]>([]);
  const [selectedKey, setSelectedKey] = useState<PassSectionKey>(PASS_SECTION_KEY.ALL);
  const [isAtTop, setIsAtTop] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  const isMobile = useDetermineMobile();
  const [emblaRef] = useEmblaCarousel({ dragFree: true, active: isMobile });
  const { isStuck } = useDetectSticky({
    ref: containerRef,
    positionTop: isMobile ? MOBILE_HEADER_START_POINT : PC_HEADER_START_POINT,
  });
  const { scrollY } = useScroll();

  const handleClick = (sectionKey: PassSectionKey) => () => {
    setSelectedKey(sectionKey);

    scrollTo(sectionKey);

    sendClickLog({
      pageCategory: WeblogPageCategory.EUROPE_TRAIN_TICKET_MAIN,
      screenName: WeblogScreenName.EUROPE_TRAIN_TICKET_MAIN,
      eventName: WeblogEventName.REGION_TAB,
      itemKind: 'button',
      itemName: sectionKey,
    });
  };

  const isSelected = (sectionKey: PassSectionKey) => {
    // 선택한 chip이 ALL이고 최상단일 때
    if (selectedKey === PASS_SECTION_KEY.ALL && isAtTop) {
      return sectionKey === PASS_SECTION_KEY.ALL;
    }
    // 그 외 inViewSectionKey 기준
    return sectionKey === inViewSectionKey;
  };

  useEffect(() => {
    return scrollY.on('change', (latest) => {
      setIsAtTop(latest <= 0);
    });
  }, [scrollY]);

  useEffect(() => {
    if (isSuccess && possibleCountries.length === 0) {
      const possibleCountries = Object.entries(classifyProductsByCountry(transportProducts))
        .filter(([, value]) => value.length > 0)
        .map(([key]) => key) as PassSectionKey[];
      setPossibleCountries([PASS_SECTION_KEY.ALL, ...possibleCountries]);
    }
  }, [isSuccess, possibleCountries]);

  return (
    <Container ref={containerRef} isStuck={isStuck}>
      <CarouselRoot ref={emblaRef}>
        <CarouselContainer>
          {possibleCountries.map((sectionKey) => (
            <AnchorChip
              key={sectionKey}
              type="button"
              isSelected={isSelected(sectionKey)}
              onClick={handleClick(sectionKey)}
            >
              <StyledText typography="paragraphBold15">{PASS_SECTION_TITLE[sectionKey]}</StyledText>
            </AnchorChip>
          ))}
        </CarouselContainer>
      </CarouselRoot>
    </Container>
  );
};

export default PassHeader;

const Container = styled.div<{ isStuck: boolean }>`
  position: sticky;
  top: ${MOBILE_HEADER_START_POINT - 2}px;
  background-color: ${({ theme }) => theme.colors.white};
  z-index: ${HEADER_Z_INDEX};

  ${media.minMobile} {
    top: ${PC_HEADER_START_POINT - 2}px;
    ${({ isStuck }) =>
      isStuck &&
      css`
        margin: 0 -24px;
        padding: 0 24px;
      `}
  }
`;

const CarouselRoot = styled.div`
  overflow: hidden;
  padding: 16px;

  ${media.minMobile} {
    padding: 12px 0;
  }
`;

const CarouselContainer = styled.div`
  display: flex;
  align-items: center;
`;

const AnchorChip = styled.button<{ isSelected?: boolean }>`
  padding: 6px 14px;
  border-radius: 16px;
  height: 32px;
  color: ${({ theme }) => theme.colors.gray.s800};

  ${({ theme, isSelected }) =>
    isSelected &&
    css`
      color: ${theme.colors.white};
      background-color: ${theme.colors.gray.s1000};
    `}
`;

const StyledText = styled(Text)`
  white-space: nowrap;
`;
